import { useEffect, useState } from "react";
import { Main } from "@/components/Main";
import { CreateLoaderTable } from "@/components/Loaders";
import {
  getStocksUnitiesSummary,
  getStocksUnitiesSummaryDetails,
  getStocksUnitiesPendingDetails,
  exportStocksUnitiesSummary,
  StockUnitiesSummaryFilters,
} from "@/Api/stocks";
import { getSelectUnities } from "@/Api/unities";
import { getSelectProductGroups } from "@/Api/productGroups";
import { getSelectContests } from "@/Api/contests";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { MultiSelectCheckbox } from "@/components/ui/multi-select-checkbox";
import {
  AlertCircle,
  ChevronDown,
  Download,
  Eye,
  FileSpreadsheet,
  FileText,
  Filter,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDateOnly } from "@/utils/format-date";
import { StockProductRow } from "@/components/StockProductRow";

interface UnitiesSummaryItem {
  contest: {
    id: number;
    uuid: string;
    name: string;
    validation_date: string;
  };
  lottery: {
    id: number;
    name: string;
    color: string;
  };
  summary: {
    total_unities: number;
    total_products: number;
    total_balance: number;
    total_pending: number;
  };
}

interface UnityProduct {
  cp_classification_id: number;
  product_name: string;
  product_classification_name: string;
  sale_price: number;
  balance: number;
}

interface UnityDetail {
  unity_id: number;
  unity_name: string;
  total_balance: number;
  products: UnityProduct[];
}

interface UnityPendingProduct {
  cp_classification_id: number;
  product_name: string;
  product_classification_name: string;
  sale_price: number;
  quota_quantity: number;
}

interface UnityPendingDetail {
  unity_id: number;
  unity_name: string;
  total_quantity: number;
  products: UnityPendingProduct[];
}

/** Primeiro e último dia do mês atual (yyyy-mm-dd, fuso local): período padrão dos filtros */
const getCurrentMonthRange = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    start_date: first.toLocaleDateString("sv-SE"),
    end_date: last.toLocaleDateString("sv-SE"),
  };
};

const ContestHeader = ({ item }: { item: UnitiesSummaryItem }) => (
  <div className="flex items-center gap-2 mb-4">
    <Badge
      className="text-xs text-white border-0"
      style={{ backgroundColor: item.lottery.color || "#000" }}
    >
      {item.lottery.name}
    </Badge>
    <span className="font-medium">{item.contest.name}</span>
  </div>
);

export const PageList = () => {
  const [data, setData] = useState<UnitiesSummaryItem[]>([]);
  const [state, setState] = useState<"loading" | "production">("loading");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [unities, setUnities] = useState<any[]>([]);
  const [lotteries, setLotteries] = useState<any[]>([]);
  const [contests, setContests] = useState<any[]>([]);

  const [filters, setFilters] = useState<StockUnitiesSummaryFilters>(() =>
    getCurrentMonthRange()
  );
  const [showFilters, setShowFilters] = useState(false);
  const [selectedUnities, setSelectedUnities] = useState<number[]>([]);
  const [selectedLotteries, setSelectedLotteries] = useState<number[]>([]);
  const [selectedContest, setSelectedContest] = useState("");
  const [startDate, setStartDate] = useState(() => getCurrentMonthRange().start_date);
  const [endDate, setEndDate] = useState(() => getCurrentMonthRange().end_date);

  const [modalItem, setModalItem] = useState<UnitiesSummaryItem | null>(null);
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [unityDetails, setUnityDetails] = useState<UnityDetail[]>([]);
  const [expandedUnities, setExpandedUnities] = useState<Set<number>>(new Set());
  const [productSearchTerms, setProductSearchTerms] = useState<Record<number, string>>({});

  const [pendingItem, setPendingItem] = useState<UnitiesSummaryItem | null>(null);
  const [isLoadingPending, setIsLoadingPending] = useState(false);
  const [pendingDetails, setPendingDetails] = useState<UnityPendingDetail[]>([]);
  const [expandedPending, setExpandedPending] = useState<Set<number>>(new Set());

  const fetchData = async (currentFilters: StockUnitiesSummaryFilters = filters) => {
    setState("loading");
    try {
      const { data: response } = await getStocksUnitiesSummary(currentFilters);
      setData(Array.isArray(response?.response) ? response.response : []);
    } catch (error) {
      console.error("Erro ao buscar estoque por unidade:", error);
      setData([]);
    } finally {
      setState("production");
    }
  };

  useEffect(() => {
    fetchData();
    (async () => {
      try {
        const [unitiesRes, lotteriesRes, contestsRes] = await Promise.all([
          getSelectUnities(),
          getSelectProductGroups(),
          getSelectContests(),
        ]);
        setUnities(unitiesRes.data?.response || []);
        setLotteries(lotteriesRes.data?.response || []);
        setContests(contestsRes.data?.response || []);
      } catch (error) {
        console.error("Erro ao buscar opções:", error);
      }
    })();
  }, []);

  const handleApplyFilters = () => {
    const newFilters: StockUnitiesSummaryFilters = {
      unity_id: selectedUnities.length > 0 ? selectedUnities : undefined,
      lottery_id: selectedLotteries.length > 0 ? selectedLotteries : undefined,
      contest_id: selectedContest ? Number(selectedContest) : undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
    };
    setFilters(newFilters);
    fetchData(newFilters);
  };

  const handleClearFilters = () => {
    const monthRange = getCurrentMonthRange();
    setSelectedUnities([]);
    setSelectedLotteries([]);
    setSelectedContest("");
    setStartDate(monthRange.start_date);
    setEndDate(monthRange.end_date);
    setFilters(monthRange);
    fetchData(monthRange);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  };

  const handleExport = async (format: "pdf" | "xlsx") => {
    setIsExporting(true);
    try {
      const blob = await exportStocksUnitiesSummary({ ...filters, format });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `estoque-unidades-${new Date().toISOString().split("T")[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Exportado com sucesso em ${format.toUpperCase()}`);
    } catch (error) {
      console.error("Error exporting:", error);
      toast.error("Erro ao exportar estoque");
    } finally {
      setIsExporting(false);
    }
  };

  const handleViewProducts = async (item: UnitiesSummaryItem) => {
    setModalItem(item);
    setUnityDetails([]);
    setExpandedUnities(new Set());
    setProductSearchTerms({});
    setIsLoadingModal(true);

    try {
      const { data: response } = await getStocksUnitiesSummaryDetails(
        item.contest.id,
        filters
      );
      setUnityDetails(Array.isArray(response?.response) ? response.response : []);
    } catch (error) {
      console.error("Erro ao buscar detalhes:", error);
    } finally {
      setIsLoadingModal(false);
    }
  };

  const handleViewPending = async (item: UnitiesSummaryItem) => {
    setPendingItem(item);
    setPendingDetails([]);
    setExpandedPending(new Set());
    setIsLoadingPending(true);

    try {
      const { data: response } = await getStocksUnitiesPendingDetails(item.contest.id);
      setPendingDetails(Array.isArray(response?.response) ? response.response : []);
    } catch (error) {
      console.error("Erro ao buscar pendentes:", error);
    } finally {
      setIsLoadingPending(false);
    }
  };

  const toggle = (
    setter: React.Dispatch<React.SetStateAction<Set<number>>>,
    id: number
  ) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Main>
      <div className="flex justify-center md:justify-between items-center flex-wrap gap-2">
        <h2 className="text-2xl font-semibold flex gap-2 items-center">
          Estoque por Unidade
        </h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw
              className={cn("h-4 w-4 mr-2", isRefreshing && "animate-spin")}
            />
            Atualizar
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={isExporting}>
                {isExporting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Exportando...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Exportar
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport("pdf")}>
                <FileText className="h-4 w-4 mr-2" />
                Exportar PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("xlsx")}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Exportar Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtros
          </Button>
        </div>
      </div>

      {showFilters && (
        <div className="bg-zinc-50 dark:bg-zinc-900 p-4 rounded-lg space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Unidade</Label>
              <MultiSelectCheckbox
                options={unities.map((unity) => ({
                  value: unity.id.toString(),
                  label: unity.name,
                }))}
                selectedValues={selectedUnities.map(String)}
                onSelectionChange={(values) =>
                  setSelectedUnities(values.map(Number))
                }
                placeholder="Selecione unidades"
                selectAllLabel="SELECIONAR TUDO"
              />
            </div>

            <div className="space-y-2">
              <Label>Loteria</Label>
              <MultiSelectCheckbox
                options={lotteries.map((lottery) => ({
                  value: lottery.id.toString(),
                  label: lottery.name,
                }))}
                selectedValues={selectedLotteries.map(String)}
                onSelectionChange={(values) =>
                  setSelectedLotteries(values.map(Number))
                }
                placeholder="Selecione loterias"
                selectAllLabel="SELECIONAR TUDO"
              />
            </div>

            <div className="space-y-2">
              <Label>Concurso</Label>
              <Select value={selectedContest} onValueChange={setSelectedContest}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um concurso" />
                </SelectTrigger>
                <SelectContent>
                  {contests.map((contest) => (
                    <SelectItem key={contest.id} value={contest.id.toString()}>
                      {contest.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Data Inicial</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Data Final</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={handleClearFilters}>
              Limpar
            </Button>
            <Button onClick={handleApplyFilters}>Aplicar Filtros</Button>
          </div>
        </div>
      )}

      {state === "loading" ? (
        <CreateLoaderTable cols={5} lines={6} />
      ) : (
        <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
          <TableHeader>
            <TableRow>
              <TableHead>Loteria</TableHead>
              <TableHead>Concurso</TableHead>
              <TableHead>Data de Validade</TableHead>
              <TableHead className="text-center">Não Aceitos</TableHead>
              <TableHead className="text-center">Produtos</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length > 0 ? (
              data.map((item) => (
                <TableRow key={`${item.contest.id}-${item.lottery.id}`}>
                  <TableCell>
                    <Badge
                      className="text-xs text-white border-0"
                      style={{ backgroundColor: item.lottery.color || "#000" }}
                    >
                      {item.lottery.name}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">{item.contest.name}</TableCell>
                  <TableCell>{formatDateOnly(item.contest.validation_date)}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center">
                      {item.summary.total_pending > 0 ? (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleViewPending(item)}
                          className="flex items-center gap-2"
                        >
                          <Eye className="h-4 w-4" />
                          {item.summary.total_pending}
                        </Button>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center">
                      {item.summary.total_products > 0 ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewProducts(item)}
                          className="flex items-center gap-2 text-gray-200 dark:text-gray-200"
                        >
                          <Eye className="h-4 w-4" />
                          {item.summary.total_products}
                        </Button>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  Nenhum estoque encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!modalItem} onOpenChange={(open) => !open && setModalItem(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Produtos:</DialogTitle>
          </DialogHeader>

          {modalItem && (
            <div className="space-y-4">
              <ContestHeader item={modalItem} />

              {isLoadingModal ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
                </div>
              ) : unityDetails.length > 0 ? (
                <div className="space-y-2">
                  {unityDetails.map((unity) => {
                    const isExpanded = expandedUnities.has(unity.unity_id);
                    const searchTerm = productSearchTerms[unity.unity_id] || "";
                    const filteredProducts = searchTerm
                      ? unity.products.filter((product) =>
                          product.product_name.replace(/^[A-Za-z]+/, "").startsWith(searchTerm)
                        )
                      : unity.products;

                    return (
                      <Collapsible
                        key={unity.unity_id}
                        className="border rounded-lg"
                        open={isExpanded}
                        onOpenChange={() => toggle(setExpandedUnities, unity.unity_id)}
                      >
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50 transition-colors rounded-lg">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-base">{unity.unity_name}</span>
                            <Badge className="bg-primary text-primary-foreground text-sm">
                              {unity.total_balance} cotas
                            </Badge>
                          </div>
                          <ChevronDown
                            className={cn(
                              "h-4 w-4 transition-transform duration-200",
                              isExpanded && "rotate-180"
                            )}
                          />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="px-4 pb-4 space-y-2">
                          <div className="flex items-center gap-2 pb-2">
                            <Label
                              htmlFor={`unity-product-search-${unity.unity_id}`}
                              className="text-sm font-medium"
                            >
                              B:
                            </Label>
                            <Input
                              id={`unity-product-search-${unity.unity_id}`}
                              type="number"
                              inputMode="numeric"
                              placeholder="Buscar por número"
                              value={searchTerm}
                              onChange={(e) =>
                                setProductSearchTerms((prev) => ({
                                  ...prev,
                                  [unity.unity_id]: e.target.value.replace(/\D/g, ""),
                                }))
                              }
                              className="max-w-[200px]"
                            />
                          </div>
                          {filteredProducts.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-4">
                              Nenhum produto encontrado com esse número
                            </p>
                          ) : (
                            filteredProducts.map((product) => (
                              <StockProductRow
                                key={product.cp_classification_id}
                                productName={product.product_name}
                                classification={product.product_classification_name}
                                quantity={product.balance}
                              />
                            ))
                          )}
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  Nenhum produto encontrado
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!pendingItem} onOpenChange={(open) => !open && setPendingItem(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Não Aceitos
            </DialogTitle>
          </DialogHeader>

          {pendingItem && (
            <div className="space-y-4">
              <ContestHeader item={pendingItem} />

              {isLoadingPending ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
                </div>
              ) : pendingDetails.length > 0 ? (
                <div className="space-y-2">
                  {pendingDetails.map((unity) => {
                    const isExpanded = expandedPending.has(unity.unity_id);
                    return (
                      <Collapsible
                        key={unity.unity_id}
                        className="border rounded-lg"
                        open={isExpanded}
                        onOpenChange={() => toggle(setExpandedPending, unity.unity_id)}
                      >
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50 transition-colors rounded-lg">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-base">{unity.unity_name}</span>
                            <Badge className="bg-red-600 text-white text-sm">
                              {unity.total_quantity} cotas
                            </Badge>
                          </div>
                          <ChevronDown
                            className={cn(
                              "h-4 w-4 transition-transform duration-200",
                              isExpanded && "rotate-180"
                            )}
                          />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="px-4 pb-4 space-y-2">
                          {unity.products.map((product, index) => (
                            <StockProductRow
                              key={`${product.cp_classification_id}-${index}`}
                              productName={product.product_name}
                              classification={product.product_classification_name}
                              quantity={product.quota_quantity}
                            />
                          ))}
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  Nenhum pendente encontrado
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Main>
  );
};
